"use client";

import React, { useState } from "react";
import { ArrowLeft, ArrowRight, Boxes, CalendarClock, CircleCheck as CheckCircle2, Cpu, Globe as Globe2, MapPin, Sparkles, Users as Users2, Wrench, ChevronRight, Compass, Lightbulb, Layers, Presentation, ShieldCheck, CircleCheck as CheckCircle, MonitorX, Tablet, Tag, Info, Users } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
interface TranslationContent {
  breadcrumb: {
    home: string;
    pathway: string;
    track: string;
    current: string;
  };
  backLink: string;
  badge: string;
  courseTitle: string;
  courseSub: string;
  description: string;
  heroCtaPrimary: string;
  heroCtaSecondary: string;
  metadata: {
    ageLabel: string;
    ageValue: string;
    platformLabel: string;
    platformValue: string;
    durationLabel: string;
    durationValue: string;
    groupLabel: string;
    groupValue: string;
  };
  targetAudience: {
    heading: string;
    subheading: string;
    ageTitle: string;
    ageDesc: string;
    prereqTitle: string;
    prereqDesc: string;
    focusTitle: string;
    focusDesc: string;
  };
  equipmentCard: {
    badge: string;
    heading: string;
    subheading: string;
    platformLabel: string;
    platformName: string;
    platformDesc: string;
    costLabel: string;
    costValue: string;
    costNote: string;
    deviceLabel: string;
    computerNote: string;
    tabletNote: string;
  };
  smallGroupModel: {
    badge: string;
    heading: string;
    subheading: string;
    intro: string;
    models: {
      ratio: string;
      title: string;
      tag: string;
      desc: string;
      highlights: string[];
    }[];
  };
  timeline: {
    heading: string;
    subheading: string;
    stages: {
      step: string;
      title: string;
      sessions: string;
      desc: string;
      focus: string[];
    }[];
  };
  methodology: {
    heading: string;
    subheading: string;
    steps: {
      number: string;
      title: string;
      desc: string;
    }[];
  };
  finalProject: {
    badge: string;
    heading: string;
    projectName: string;
    desc: string;
    highlights: string[];
  };
  platformSpec: {
    heading: string;
    badge: string;
    platformName: string;
    desc: string;
    specs: {
      label: string;
      value: string;
    }[];
  };
  pathwayMap: {
    heading: string;
    subheading: string;
    currentLabel: string;
    nodes: {
      code: string;
      name: string;
      url?: string;
      isCurrent?: boolean;
    }[];
  };
  skillsHeading: string;
  engineeringSkillsLabel: string;
  codingSkillsLabel: string;
  skills: {
    engineering: string[];
    coding: string[];
  };
  cityHighlightsHeading: string;
  cityHighlights: {
    title: string;
    desc: string;
  }[];
  whatsNext: {
    heading: string;
    subheading: string;
    courseCode: string;
    courseTitle: string;
    desc: string;
    cta: string;
  };
  prevCourse: string;
  nextCourse: string;
}
const COURSE_THUMBNAILS: Record<string, {
  src: string;
  srcset: string;
  sizes: string;
}> = {
  D1: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d1.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d1.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d1.png-wvc-sizes"
  },
  D2: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d2.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d2.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d2.png-wvc-sizes"
  },
  D3: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d3.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d3.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d3.png-wvc-sizes"
  },
  D4: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d4.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d4.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d4.png-wvc-sizes"
  },
  HP1: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp1.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp1.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp1.webp-wvc-sizes"
  },
  HP2: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp2.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp2.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp2.png-wvc-sizes"
  },
  HP3: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp3.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp3.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp3.png-wvc-sizes"
  },
  HP4: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp4.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp4.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp4.png-wvc-sizes"
  },
  HP5: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp5.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp5.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp5.png-wvc-sizes"
  },
  HP6: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp6.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp6.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp6.png-wvc-sizes"
  }
};
const CONTENT: Record<"vi" | "en", TranslationContent> = {
  vi: {
    breadcrumb: {
      home: "Trang chủ",
      pathway: "Lộ trình Robotics",
      track: "Robotics 4–6 tuổi",
      current: "D2 – Thành phố của em"
    },
    backLink: "QUAY LẠI LỘ TRÌNH ROBOTICS",
    badge: "Khóa học Robotics Mầm non • Cấp độ D2",
    courseTitle: "Thành phố của em",
    courseSub: "Chế tạo công trình thông minh & khám phá đô thị hiện đại",
    description: "Tiếp nối sau cấp độ D1, các kỹ sư nhí bước vào hành trình quy hoạch và vận hành mô hình đô thị sôi động. Học sinh ứng dụng các cơ cấu chuyển động cơ khí kết hợp lập trình trực quan để giải quyết các vấn đề thực tế trong thành phố.",
    heroCtaPrimary: "ĐĂNG KÝ TƯ VẤN",
    heroCtaSecondary: "XEM TOÀN BỘ LỘ TRÌNH",
    metadata: {
      ageLabel: "Độ tuổi phù hợp",
      ageValue: "4–5 tuổi sau D1 / 5 tuổi",
      platformLabel: "Nền tảng học tập",
      platformValue: "LEGO® Education Duplo",
      durationLabel: "Thời lượng khóa",
      durationValue: "12 buổi × 90 phút",
      groupLabel: "Quy mô nhóm",
      groupValue: "2–3 học sinh / bộ kit"
    },
    targetAudience: {
      heading: "Khóa học này dành cho ai?",
      subheading: "Lộ trình phát triển năng lực tiếp nối tự nhiên từ nền tảng D1",
      ageTitle: "Trẻ 4–5 tuổi (sau khi hoàn thành D1) hoặc 5 tuổi",
      ageDesc: "Đã quen thuộc với khối gạch lắp ráp, có khả năng tập trung 60–90 phút trong nhóm học nhỏ.",
      prereqTitle: "Kế thừa nền tảng cơ bản từ D1",
      prereqDesc: "Tiếp tục phát triển tư duy hình học không gian và sự khéo léo của các ngón tay.",
      focusTitle: "Nâng cấp cơ cấu & chuỗi lệnh logic",
      focusDesc: "Sẵn sàng làm quen với bánh răng truyền động, đòn bẩy trợ lực và câu lệnh điều kiện nếu... thì."
    },
    equipmentCard: {
      badge: "TRANG BỊ & HỌC CỤ",
      heading: "Thiết bị & Nền tảng học tập",
      subheading: "Chuẩn hóa công cụ trực quan và an toàn tuyệt đối cho trẻ nhỏ",
      platformLabel: "Nền tảng chính thức",
      platformName: "LEGO® Education Duplo",
      platformDesc: "Khối gạch cỡ lớn bo tròn an toàn tối đa cho trẻ 4–6 tuổi, kết hợp các chi tiết kỹ thuật cơ khí như bánh răng, trục xoay và đòn bẩy.",
      costLabel: "Chi phí thiết bị tham khảo",
      costValue: "~1.000.000 VNĐ / bộ kit",
      costNote: "Lưu ý: Đây là chi phí thiết bị tham khảo để trang bị bộ kit học tập, KHÔNG PHẢI là học phí khóa học.",
      deviceLabel: "Yêu cầu thiết bị điện tử",
      computerNote: "KHÔNG yêu cầu máy tính cá nhân hay laptop trong suốt khóa học.",
      tabletNote: "Máy tính bảng (tablet) chỉ là thiết bị bổ trợ tùy chọn, không bắt buộc."
    },
    smallGroupModel: {
      badge: "MÔ HÌNH HỌC TẬP",
      heading: "HỌC ROBOTICS THEO NHÓM NHỎ",
      subheading: "SMALL-GROUP ROBOTICS LEARNING",
      intro: "Phương pháp học tập thực hành thông qua tương tác nhóm nhỏ giúp trẻ rèn luyện tư duy kỹ thuật tự nhiên. Mô hình học nhóm tạo môi trường cùng thảo luận, chia sẻ học cụ và phối hợp giải quyết thử thách lắp ráp mà không đặt nặng việc so sánh tính vượt trội giữa các hình thức.",
      models: [{
        ratio: "2 học sinh / bộ kit",
        title: "Mô hình 2 học sinh / bộ kit",
        tag: "Mô hình lý tưởng",
        desc: "Tối ưu hóa thời gian thực hành cá nhân và tăng cường sự phối hợp chặt chẽ giữa đôi bạn.",
        highlights: ["Tăng tối đa thời lượng thao tác và lắp ráp trực tiếp trên từng chi tiết", "Dễ dàng thảo luận, phân chia nhiệm vụ và phối hợp ăn ý", "Cơ hội thực hành và kiểm thử sâu sắc từng cơ cấu chuyển động"]
      }, {
        ratio: "3 học sinh / bộ kit",
        title: "Mô hình 3 học sinh / bộ kit",
        tag: "Mô hình tối ưu chi phí",
        desc: "Đảm bảo đầy đủ trải nghiệm học tập cộng tác theo nhóm đồng thời tối ưu hóa chi phí học cụ.",
        highlights: ["Duy trì nguyên vẹn môi trường học tập tương tác và làm việc nhóm", "Tối ưu hóa ngân sách đầu tư thiết bị và học cụ trên mỗi học sinh", "Khuyến khích trẻ rèn luyện kỹ năng chia sẻ, lắng nghe và luân phiên vai trò"]
      }]
    },
    timeline: {
      heading: "Cấu trúc 12 buổi học theo giai đoạn",
      subheading: "Chủ đề quy hoạch & vận hành đô thị thông minh",
      stages: [{
        step: "01",
        title: "Khởi động & Làm quen đô thị",
        sessions: "Buổi 1 – 2",
        desc: "Nhận biết quy luật tổ chức giao thông, ranh giới không gian và vai trò của các công trình công cộng.",
        focus: ["Định hình chủ đề đô thị", "Quy tắc an toàn lắp ghép"]
      }, {
        step: "02",
        title: "Xây dựng cơ cấu kỹ thuật",
        sessions: "Buổi 3 – 5",
        desc: "Tích hợp các cơ cấu cơ học thực tế vào mô hình tòa nhà, cầu đường và trạm kiểm soát giao thông.",
        focus: ["Bánh răng truyền động", "Đòn bẩy trợ lực", "Cơ cấu nâng – hạ"]
      }, {
        step: "03",
        title: "Tư duy thuật toán & Điều hướng",
        sessions: "Buổi 6 – 8",
        desc: "Thiết lập chuỗi lệnh điều khiển các phương tiện giao thông và hệ thống đèn tín hiệu đô thị.",
        focus: ["Chuỗi lệnh 4–6 bước", "Điều kiện: Nếu... thì...", "Thuật toán tìm đường"]
      }, {
        step: "04",
        title: "Thử nghiệm & Debugging",
        sessions: "Buổi 9 – 10",
        desc: "Cho mô hình vận hành thử nghiệm trên bản đồ giao thông phức tạp, phát hiện lỗi và tối ưu hóa giải pháp.",
        focus: ["Phát hiện sai lệch chuỗi lệnh", "Sửa lỗi khớp bánh răng", "Hoàn thiện ăn khớp"]
      }, {
        step: "05",
        title: "Trình bày & Triển lãm đô thị",
        sessions: "Buổi 11 – 12",
        desc: "Kết nối các công trình thành một cụm thành phố hoàn chỉnh, tự tin thuyết minh cơ chế hoạt động.",
        focus: ["Thuyết trình dự án nhí", "Vận hành phối hợp nhóm", "Nhận chứng chỉ D2"]
      }]
    },
    methodology: {
      heading: "Phương pháp học 4 bước độc quyền",
      subheading: "Mô hình sư phạm chuẩn quốc tế giúp trẻ chủ động khám phá",
      steps: [{
        number: "01",
        title: "Khởi động",
        desc: "Đặt vấn đề qua câu chuyện tình huống thực tế về cuộc sống đô thị và đặt câu hỏi kích thích tò mò."
      }, {
        number: "02",
        title: "Lắp ráp",
        desc: "Trực tiếp thử nghiệm lắp ghép khối gạch, bánh răng và đòn bẩy để tìm ra giải pháp kết cấu vững chắc."
      }, {
        number: "03",
        title: "Lập trình / Tư duy",
        desc: "Xây dựng chuỗi thẻ lệnh logic trực quan, phân tích điều kiện rẽ nhánh và kiểm thử đường đi."
      }, {
        number: "04",
        title: "Trình bày",
        desc: "Tập diễn đạt ý tưởng kỹ thuật trước bạn bè, giải thích nguyên lý vận hành của mô hình do mình làm ra."
      }]
    },
    finalProject: {
      badge: "Dự án cuối khóa",
      heading: "Sản phẩm thực tế cuối khóa",
      projectName: "Thành phố tí hon",
      desc: "Học sinh ứng dụng toàn bộ kiến thức 12 buổi để thiết kế và lắp ráp một cụm đô thị thu nhỏ hoàn chỉnh, tích hợp hệ thống truyền động bánh răng liên hoàn và đòn bẩy nâng hạ vật liệu.",
      highlights: ["Hệ thống cầu xoay và rào chắn ga tự động phối hợp", "Thang máy cơ học nâng hạ vật tư thi công", "Tuyến xe tuần tra tìm đường với chuỗi lệnh 4–6 bước", "Mô phỏng quy hoạch giao thông an toàn và văn minh"]
    },
    platformSpec: {
      heading: "Nền tảng & Thiết bị học tập",
      badge: "Phần cứng tiêu chuẩn",
      platformName: "LEGO® Education Duplo",
      desc: "Bộ công cụ giáo dục được thiết kế kích thước lớn, bo tròn an toàn tối đa cho trẻ 4–6 tuổi, kết hợp các chi tiết kỹ thuật chuyên biệt như trục xoay, bánh răng răng cưa và đòn bẩy lực.",
      specs: [{
        label: "Nền tảng cốt lõi",
        value: "Duplo"
      }, {
        label: "Mức độ an toàn",
        value: "Chứng nhận vật liệu chuẩn EN71 & ASTM"
      }, {
        label: "Chi tiết kỹ thuật",
        value: "Bánh răng lớn, trục chốt, thanh đòn bẩy, ròng rọc"
      }, {
        label: "Hình thức điều khiển",
        value: "Thẻ lệnh trực quan & mã hóa màu sắc"
      }]
    },
    pathwayMap: {
      heading: "Vị trí trong lộ trình Robotics",
      subheading: "Lộ trình 10 cấp độ liên tục từ 4 đến 16 tuổi tại ROBOTO",
      currentLabel: "Đang xem: D2",
      nodes: [{
        code: "D1",
        name: "Bé làm kỹ sư",
        url: "/RoboticsCourseD1Page"
      }, {
        code: "D2",
        name: "Thành phố của em",
        isCurrent: true
      }, {
        code: "D3",
        name: "Cỗ máy kỳ diệu",
        url: "/RoboticsCourseD3Page"
      }, {
        code: "D4",
        name: "Thế giới chuyển động",
        url: "/RoboticsCourseD4Page"
      }, {
        code: "HP1",
        name: "Kỹ sư nhí khám phá",
        url: "/RoboticsCourseHP1Page"
      }, {
        code: "HP2",
        name: "Cơ cấu truyền động",
        url: "/RoboticsCourseHP2Page"
      }, {
        code: "HP3",
        name: "Tự động hóa thông minh",
        url: "/RoboticsCourseHP3Page"
      }, {
        code: "HP4",
        name: "Cảm biến & Thuật toán",
        url: "/RoboticsCourseHP4Page"
      }, {
        code: "HP5",
        name: "Robot thi đấu",
        url: "/RoboticsCourseHp5Page"
      }, {
        code: "HP6",
        name: "Sáng chế chuyên sâu",
        url: "/RoboticsCourseHP6Page"
      }]
    },
    skillsHeading: "Kỹ năng & Kiến thức trọng tâm",
    engineeringSkillsLabel: "Cơ khí & Kỹ thuật",
    codingSkillsLabel: "Tư duy Lập trình & Logic",
    skills: {
      engineering: ["Bánh răng truyền động", "Đòn bẩy lực học", "Cơ cấu nâng – hạ"],
      coding: ["Chuỗi lệnh 4–6 bước", "Vòng lặp cơ bản", "Điều kiện: Nếu… thì…", "Thuật toán tìm đường", "Tìm & sửa lỗi (Debugging)"]
    },
    cityHighlightsHeading: "Dự án mô phỏng đô thị thực tế",
    cityHighlights: [{
      title: "Cầu xoay & Rào chắn ga xe lửa",
      desc: "Ứng dụng đòn bẩy và bánh răng để điều khiển giao thông an toàn khi tàu đến."
    }, {
      title: "Thang máy nâng hạ công trường",
      desc: "Lắp ráp ròng rọc và hệ trục quay để vận chuyển vật liệu xây dựng lên cao tầng."
    }, {
      title: "Xe tuần tra tự hành tìm đường",
      desc: "Thiết lập chuỗi lệnh logic và sửa lỗi thuật toán để xe hoàn thành lịch trình."
    }],
    whatsNext: {
      heading: "Bước tiếp theo của con là gì?",
      subheading: "Cấp độ nâng cao sau khi làm chủ kiến thức nền tảng D2",
      courseCode: "CẤP ĐỘ D3",
      courseTitle: "D3 – Cỗ máy kỳ diệu",
      desc: "Con sẽ khám phá các cơ cấu bánh răng hành tinh, ròng rọc kép và bộ cảm ứng màu sắc để xây dựng các cỗ máy phức hợp.",
      cta: "KHÁM PHÁ KHÓA D3"
    },
    prevCourse: "← D1 — Bé làm kỹ sư",
    nextCourse: "D3 — Cỗ máy kỳ diệu →"
  },
  en: {
    breadcrumb: {
      home: "Home",
      pathway: "Robotics Learning Pathway",
      track: "Robotics 4–6 Years",
      current: "D2 – My City"
    },
    backLink: "BACK TO ROBOTICS PATHWAY",
    badge: "Early Robotics Course • Level D2",
    courseTitle: "My City",
    courseSub: "Smart City Construction & Urban Mechanism Exploration",
    description: "Following Course D1, young engineers embark on building and running an active mini metropolis. Students apply fundamental mechanical movements coupled with visual programming blocks to solve practical city challenges.",
    heroCtaPrimary: "BOOK CONSULTATION",
    heroCtaSecondary: "VIEW FULL PATHWAY",
    metadata: {
      ageLabel: "Target Age",
      ageValue: "4–5 yrs (post D1) / 5 yrs",
      platformLabel: "Learning Platform",
      platformValue: "LEGO® Education Duplo",
      durationLabel: "Course Length",
      durationValue: "12 sessions × 90 mins",
      groupLabel: "Group Ratio",
      groupValue: "2–3 students / kit"
    },
    targetAudience: {
      heading: "Who is this course for?",
      subheading: "A purposeful learning continuum advancing seamlessly from D1",
      ageTitle: "Children aged 4–5 (after D1) or 5 years old",
      ageDesc: "Comfortable handling builder bricks and able to stay actively engaged for 60–90 minute sessions in small groups.",
      prereqTitle: "Building on D1's Foundation",
      prereqDesc: "Further strengthening spatial visual acumen, fine-motor dexterity, and curiosity.",
      focusTitle: "Gears, Levers & Conditional Logic",
      focusDesc: "Ready to explore mechanical gear trains, mechanical levers, and introductory if... then algorithmic logic."
    },
    equipmentCard: {
      badge: "EQUIPMENT & HARDWARE",
      heading: "Course Learning Equipment",
      subheading: "Standardized hands-on toolkits with high safety criteria for young builders",
      platformLabel: "Core Educational Platform",
      platformName: "LEGO® Education Duplo",
      platformDesc: "Child-safe, oversized modular building system engineered for ages 4–6, integrated with mechanical gears, axles, and levers.",
      costLabel: "Reference Equipment Cost",
      costValue: "~1,000,000 VNĐ / kit",
      costNote: "Please note: This is the reference equipment kit cost, NOT the course tuition.",
      deviceLabel: "Digital Device Requirements",
      computerNote: "Computer or desktop laptop is NOT required throughout this course.",
      tabletNote: "Tablet is an optional supplementary device only and is not mandatory."
    },
    smallGroupModel: {
      badge: "LEARNING FRAMEWORK",
      heading: "SMALL-GROUP ROBOTICS LEARNING",
      subheading: "HỌC ROBOTICS THEO NHÓM NHỎ",
      intro: "Hands-on robotics learning thrives in intimate, small-group settings where children collaborate, exchange ideas, and construct functional machines together. Both group options offer purposeful, active teamwork tailored to different operational needs without claiming either model is superior.",
      models: [{
        ratio: "2 students / kit",
        title: "2 Students / Kit Model",
        tag: "Ideal Model",
        desc: "Maximizes direct manipulation time per learner with seamless peer-to-peer cooperation.",
        highlights: ["More direct hands-on building time for each child on every mechanical component", "Effortless partner communication and intuitive role-sharing", "Deeper trial-and-error testing of kinetic mechanisms and logic routines"]
      }, {
        ratio: "3 students / kit",
        title: "3 Students / Kit Model",
        tag: "Cost-Optimized Model",
        desc: "Maintains authentic collaborative learning while optimizing hardware allocation costs.",
        highlights: ["Preserves authentic team-based problem-solving and peer discussion", "Optimizes per-student kit hardware investment for families and centers", "Encourages sharing, patient listening, and constructive task-rotation habits"]
      }]
    },
    timeline: {
      heading: "12-Session Phased Curriculum Structure",
      subheading: "Urban infrastructure and mechanical civic themes",
      stages: [{
        step: "01",
        title: "Warm-up & Civic Exploration",
        sessions: "Sessions 1 – 2",
        desc: "Discovering urban layout concepts, zoning fundamentals, and cooperative community routines.",
        focus: ["Urban theme orientation", "Collaborative building rules"]
      }, {
        step: "02",
        title: "Mechanical Engineering Builds",
        sessions: "Sessions 3 – 5",
        desc: "Integrating functional kinetic mechanisms into building frames, road barriers, and depots.",
        focus: ["Gear drive trains", "Lever mechanics", "Lift & lower mechanisms"]
      }, {
        step: "03",
        title: "Algorithmic Thinking & Routing",
        sessions: "Sessions 6 – 8",
        desc: "Mapping algorithmic direction sequences to move city emergency and transport units reliably.",
        focus: ["4–6 step command sequences", "If... then... logic", "Pathfinding sequences"]
      }, {
        step: "04",
        title: "Testing & Systematic Debugging",
        sessions: "Sessions 9 – 10",
        desc: "Running stress tests on navigation mats, troubleshooting mechanical friction and command slips.",
        focus: ["Logic sequence verification", "Gear mesh adjustment", "Friction reduction"]
      }, {
        step: "05",
        title: "Presentation & Civic Expo",
        sessions: "Sessions 11 – 12",
        desc: "Interlinking individual city sections into a joint mega-city, presenting mechanical workings proudly.",
        focus: ["Junior project presentation", "Coordinated city operation", "D2 completion certificate"]
      }]
    },
    methodology: {
      heading: "4-Step Learning Methodology",
      subheading: "Proven pedagogical framework cultivating hands-on mastery",
      steps: [{
        number: "01",
        title: "Warm-up",
        desc: "Introducing real-world city challenges through guided stories that spark curiosity and questions."
      }, {
        number: "02",
        title: "Assemble",
        desc: "Iterative physical assembly pairing structural Duplo bricks with kinetic gears and levers."
      }, {
        number: "03",
        title: "Coding / Logic",
        desc: "Creating sequenced command routes, testing branch choices, and diagnosing early algorithmic bugs."
      }, {
        number: "04",
        title: "Present",
        desc: "Explaining how their model operates in front of peers, building confidence and technical vocabulary."
      }]
    },
    finalProject: {
      badge: "Final Course Milestone",
      heading: "Capstone Showcase Project",
      projectName: "Tiny City",
      desc: "Students synthesize all 12 sessions of learning to assemble an interlinked urban landscape featuring synchronized gear trains and lift-lower hoisting levers.",
      highlights: ["Synchronized swing bridges and railroad warning barriers", "Mechanical vertical construction hoist and crane rigging", "Autonomous patrol vehicle navigating 4–6 step logic routes", "Realistic simulation of civic transport and public utility flow"]
    },
    platformSpec: {
      heading: "Platform & Equipment Specifications",
      badge: "Hardware Standard",
      platformName: "LEGO® Education Duplo",
      desc: "Oversized, child-safe modular building system engineered for ages 4–6, augmented with specialized engineering axles, gear wheels, and mechanical levers.",
      specs: [{
        label: "Core Platform",
        value: "Duplo"
      }, {
        label: "Safety Standard",
        value: "Certified non-toxic, EN71 & ASTM compliant"
      }, {
        label: "Mechanical Parts",
        value: "High-contrast gears, axles, levers, hoist pulleys"
      }, {
        label: "Control Format",
        value: "Visual directional cards & color-coded logic tiles"
      }]
    },
    pathwayMap: {
      heading: "Position in Robotics Pathway",
      subheading: "Continuous 10-level engineering progression from ages 4 to 16",
      currentLabel: "Current Stage: D2",
      nodes: [{
        code: "D1",
        name: "Junior Engineer",
        url: "/RoboticsCourseD1Page"
      }, {
        code: "D2",
        name: "My City",
        isCurrent: true
      }, {
        code: "D3",
        name: "Wonder Machines",
        url: "/RoboticsCourseD3Page"
      }, {
        code: "D4",
        name: "Moving World",
        url: "/RoboticsCourseD4Page"
      }, {
        code: "HP1",
        name: "Junior Explorer",
        url: "/RoboticsCourseHP1Page"
      }, {
        code: "HP2",
        name: "Kinetic Mechanisms",
        url: "/RoboticsCourseHP2Page"
      }, {
        code: "HP3",
        name: "Smart Automation",
        url: "/RoboticsCourseHP3Page"
      }, {
        code: "HP4",
        name: "Sensors & Algorithms",
        url: "/RoboticsCourseHP4Page"
      }, {
        code: "HP5",
        name: "Competition Robotics",
        url: "/RoboticsCourseHp5Page"
      }, {
        code: "HP6",
        name: "Advanced Engineering",
        url: "/RoboticsCourseHP6Page"
      }]
    },
    skillsHeading: "Core Competencies & Skills",
    engineeringSkillsLabel: "Mechanics & Engineering",
    codingSkillsLabel: "Coding & Algorithmic Logic",
    skills: {
      engineering: ["Gear drive trains", "Lever mechanics", "Lift & lower mechanisms"],
      coding: ["4–6 step command sequences", "Basic loops & repetition", "If… then… conditional logic", "Pathfinding sequences", "System debugging"]
    },
    cityHighlightsHeading: "Realistic Urban Projects",
    cityHighlights: [{
      title: "Swing Bridges & Rail Gates",
      desc: "Using levers and gear pairings to coordinate safe transit when trains approach."
    }, {
      title: "Construction Material Lift",
      desc: "Building vertical hoist rigs and cranks to move supplies safely up high-rises."
    }, {
      title: "Autonomous City Patrol Route",
      desc: "Structuring step-by-step algorithms and debugging path detours through town."
    }],
    whatsNext: {
      heading: "What's Next for Your Child?",
      subheading: "The next developmental leap once D2 competencies are mastered",
      courseCode: "COURSE LEVEL D3",
      courseTitle: "D3 — Wonder Machines",
      desc: "Students discover planetary gears, compound pulleys, and color-sensor interaction to build multifaceted automated machines.",
      cta: "EXPLORE COURSE D3"
    },
    prevCourse: "← D1 — Junior Engineer",
    nextCourse: "D3 — Wonder Machines →"
  }
};
export default function CourseOverview() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  const t = CONTENT[lang];
  return <section id="roboticscoursed2overview" data-nav="light" className="relative bg-background text-foreground py-12 md:py-20 lg:py-24 border-b border-border overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        {/* (1) Four-Level Breadcrumb */}
        <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-muted-foreground mb-6">
          <Link to="/" className="hover:text-primary transition-colors font-medium hover:underline">
            {t.breadcrumb.home}
          </Link>
          <ChevronRight className="size-3.5 text-muted-foreground/60 shrink-0" />
          <Link to="/RoboticsLearningPathwayPage" className="hover:text-primary transition-colors font-medium hover:underline">
            {t.breadcrumb.pathway}
          </Link>
          <ChevronRight className="size-3.5 text-muted-foreground/60 shrink-0" />
          <Link to="#" className="hover:text-primary transition-colors font-medium hover:underline">
            {t.breadcrumb.track}
          </Link>
          <ChevronRight className="size-3.5 text-muted-foreground/60 shrink-0" />
          <span className="font-semibold text-foreground truncate">
            {t.breadcrumb.current}
          </span>
        </nav>

        {/* Navigation & Language Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <Link to="/RoboticsLearningPathwayPage" className="inline-flex items-center gap-2 text-xs md:text-sm font-semibold tracking-wide text-primary hover:text-primary/80 transition-colors uppercase">
            <ArrowLeft className="size-4" />
            <span>{t.backLink}</span>
          </Link>

          {/* VI / EN Toggle */}
          <div className="inline-flex items-center gap-1 rounded-lg border border-border bg-card p-1 shadow-2xs">
            <Globe2 className="size-3.5 ml-2 text-muted-foreground mr-1" />
            <Button type="button" variant="ghost" size="sm" onClick={() => setLang("vi")} className={`h-auto px-3 py-1 rounded-lg text-xs font-semibold transition-all ${lang === "vi" ? "bg-primary text-primary-foreground shadow-2xs hover:bg-primary hover:text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-transparent"}`} aria-label="Chuyển sang tiếng Việt">
              VI
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setLang("en")} className={`h-auto px-3 py-1 rounded-lg text-xs font-semibold transition-all ${lang === "en" ? "bg-primary text-primary-foreground shadow-2xs hover:bg-primary hover:text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-transparent"}`} aria-label="Switch to English">
              EN
            </Button>
          </div>
        </div>

        {/* Hero Visual Banner: Duplo Mechanical City Showcase */}
        <div className="relative rounded-2xl overflow-hidden border border-border bg-card shadow-md mb-10 lg:mb-12">
          <div className="relative h-48 sm:h-64 md:h-80 lg:h-96 w-full">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_d2_duplo_city_hero.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_d2_duplo_city_hero.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_d2_duplo_city_hero.png-wvc-sizes" alt="Duplo city-building scene with gears, levers and simple mechanical city models" loading="lazy" decoding="async" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-background/95 via-background/30 to-transparent flex items-end p-4 sm:p-6 lg:p-8">
              <div className="flex flex-wrap items-center justify-between gap-4 w-full bg-card/90 backdrop-blur-md p-4 sm:p-5 rounded-xl border border-border shadow-xs">
                <div className="flex items-center gap-3.5">
                  <div className="size-10 sm:size-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <Boxes className="size-5 sm:size-6" />
                  </div>
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-primary block">
                      {lang === "vi" ? "MÔ HÌNH THÍ NGHIỆM ĐÔ THỊ" : "DUPLO MECHANICAL CITY MODEL"}
                    </span>
                    <h2 className="text-sm sm:text-base font-bold text-foreground">
                      {lang === "vi" ? "Mô hình quy hoạch đô thị với bánh răng truyền động & đòn bẩy cơ học" : "Duplo city-building scene with gears, levers & mechanical city models"}
                    </h2>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs font-bold border-primary/30 text-primary bg-primary/5">
                    DUPLO MECHANICS
                  </Badge>
                  <Badge variant="secondary" className="text-xs font-bold">
                    LEVEL D2
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Hero Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center mb-12 lg:mb-16">
          <div className="lg:col-span-7 space-y-5">
            <div className="flex flex-wrap items-center gap-2.5">
              <Badge variant="outline" className="px-3 py-1 text-xs sm:text-sm font-bold bg-primary/10 text-primary border-primary/20 rounded-md">
                MÃ KHÓA: D2
              </Badge>
              <Badge variant="secondary" className="px-3 py-1 text-xs sm:text-sm font-semibold rounded-md">
                {t.badge}
              </Badge>
            </div>

            <div className="space-y-2">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-foreground font-serif">
                {t.courseTitle}
              </h1>
              <p className="text-lg sm:text-xl font-medium text-primary">
                {t.courseSub}
              </p>
            </div>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
              {t.description}
            </p>

            {/* Both Hero CTAs: Primary & Secondary */}
            <div className="pt-2 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="rounded-md shadow-sm">
                <Link to="/FreeTrialContactPage">
                  <Sparkles className="size-4 mr-2" />
                  {t.heroCtaPrimary}
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-md border-primary text-primary hover:bg-primary hover:text-primary-foreground font-semibold">
                <Link to="/RoboticsLearningPathwayPage">
                  <Compass className="size-4 mr-2" />
                  {t.heroCtaSecondary}
                </Link>
              </Button>
            </div>
          </div>

          {/* City Visual Theme Card */}
          <div className="lg:col-span-5">
            <div className="relative rounded-xl overflow-hidden border border-border bg-card shadow-md">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_d2_city_theme.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_d2_city_theme.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_d2_city_theme.png-wvc-sizes" alt="Robotics Course D2 My City LEGO Duplo cityscape simulation" loading="lazy" decoding="async" className="w-full h-64 sm:h-72 object-cover" />
              <div className="p-5 bg-card/95 backdrop-blur-sm border-t border-border flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="size-10 rounded-lg bg-accent/20 text-accent-foreground flex items-center justify-center shrink-0">
                    <MapPin className="size-5 text-accent" />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-foreground">
                      {lang === "vi" ? "Chủ đề Đô thị Sáng tạo" : "Urban Theme Studio"}
                    </h2>
                    <p className="text-xs text-muted-foreground">
                      {lang === "vi" ? "Mô hình giao thông & hạ tầng nhí" : "Transit & infrastructure mini builds"}
                    </p>
                  </div>
                </div>
                <Badge variant="outline" className="text-xs border-border font-semibold">
                  LEGO® Duplo
                </Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Course Fast Specs Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12 lg:mb-16">
          <Card className="border-border bg-card shadow-2xs">
            <CardContent className="p-4 sm:p-5 flex items-start gap-3.5">
              <div className="p-2.5 rounded-lg bg-primary/10 text-primary shrink-0">
                <Users2 className="size-5" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground uppercase font-semibold tracking-wider">
                  {t.metadata.ageLabel}
                </p>
                <p className="text-sm sm:text-base font-bold text-foreground truncate mt-0.5">
                  {t.metadata.ageValue}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border bg-card shadow-2xs">
            <CardContent className="p-4 sm:p-5 flex items-start gap-3.5">
              <div className="p-2.5 rounded-lg bg-secondary/20 text-secondary-foreground shrink-0">
                <Boxes className="size-5 text-secondary" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground uppercase font-semibold tracking-wider">
                  {t.metadata.platformLabel}
                </p>
                <p className="text-sm sm:text-base font-bold text-foreground truncate mt-0.5">
                  {t.metadata.platformValue}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border bg-card shadow-2xs">
            <CardContent className="p-4 sm:p-5 flex items-start gap-3.5">
              <div className="p-2.5 rounded-lg bg-accent/20 text-accent-foreground shrink-0">
                <CalendarClock className="size-5 text-accent" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground uppercase font-semibold tracking-wider">
                  {t.metadata.durationLabel}
                </p>
                <p className="text-sm sm:text-base font-bold text-foreground truncate mt-0.5">
                  {t.metadata.durationValue}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border bg-card shadow-2xs">
            <CardContent className="p-4 sm:p-5 flex items-start gap-3.5">
              <div className="p-2.5 rounded-lg bg-muted text-foreground shrink-0">
                <Cpu className="size-5" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground uppercase font-semibold tracking-wider">
                  {t.metadata.groupLabel}
                </p>
                <p className="text-sm sm:text-base font-bold text-foreground truncate mt-0.5">
                  {t.metadata.groupValue}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* (2) 'Khóa học này dành cho ai?' / 'Who is this course for?' */}
        <div className="mb-12 lg:mb-16">
          <div className="border border-border rounded-xl bg-card p-6 sm:p-8 lg:p-10 shadow-xs">
            <div className="max-w-3xl mb-8">
              <Badge variant="outline" className="text-xs font-bold text-primary mb-2">
                {lang === "vi" ? "ĐỐI TƯỢNG PHÙ HỢP" : "TARGET AUDIENCE"}
              </Badge>
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground font-serif">
                {t.targetAudience.heading}
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mt-1">
                {t.targetAudience.subheading}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="border-border bg-muted/30 shadow-2xs hover:border-primary/40 transition-colors">
                <CardContent className="p-5 sm:p-6 space-y-3">
                  <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-bold text-lg">
                    4–5
                  </div>
                  <h3 className="text-base font-bold text-foreground">
                    {t.targetAudience.ageTitle}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {t.targetAudience.ageDesc}
                  </p>
                </CardContent>
              </Card>

              <Card className="border-border bg-muted/30 shadow-2xs hover:border-secondary/40 transition-colors">
                <CardContent className="p-5 sm:p-6 space-y-3">
                  <div className="size-10 rounded-lg bg-secondary/20 text-secondary flex items-center justify-center">
                    <Layers className="size-5" />
                  </div>
                  <h3 className="text-base font-bold text-foreground">
                    {t.targetAudience.prereqTitle}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {t.targetAudience.prereqDesc}
                  </p>
                </CardContent>
              </Card>

              <Card className="border-border bg-muted/30 shadow-2xs hover:border-accent/40 transition-colors">
                <CardContent className="p-5 sm:p-6 space-y-3">
                  <div className="size-10 rounded-lg bg-accent/20 text-accent flex items-center justify-center">
                    <Wrench className="size-5" />
                  </div>
                  <h3 className="text-base font-bold text-foreground">
                    {t.targetAudience.focusTitle}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {t.targetAudience.focusDesc}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* New Block 1: Equipment & Hardware Card */}
        <div className="mb-12 lg:mb-16">
          <div className="border border-border rounded-xl bg-card p-6 sm:p-8 lg:p-10 shadow-xs">
            <div className="max-w-3xl mb-8">
              <Badge variant="outline" className="text-xs font-bold text-secondary mb-2">
                {t.equipmentCard.badge}
              </Badge>
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground font-serif">
                {t.equipmentCard.heading}
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mt-1">
                {t.equipmentCard.subheading}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Platform Duplo Card */}
              <Card className="border-border bg-muted/30 shadow-2xs hover:border-secondary/50 transition-colors">
                <CardContent className="p-5 sm:p-6 space-y-3">
                  <div className="overflow-hidden rounded-lg border border-border bg-background mb-2">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/duplo_robotics_kit.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/duplo_robotics_kit.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/duplo_robotics_kit.png-wvc-sizes" alt="LEGO Duplo STEAM robotics kit used in ROBOTO's D1–D4 foundation track" loading="lazy" decoding="async" className="w-full h-36 object-cover" />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="size-10 rounded-lg bg-secondary/20 text-secondary flex items-center justify-center">
                      <Boxes className="size-5" />
                    </div>
                    <Badge variant="secondary" className="text-xs font-semibold">
                      Duplo
                    </Badge>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      {t.equipmentCard.platformLabel}
                    </p>
                    <h3 className="text-base sm:text-lg font-bold text-foreground mt-1">
                      {t.equipmentCard.platformName}
                    </h3>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {t.equipmentCard.platformDesc}
                  </p>
                </CardContent>
              </Card>

              {/* Reference Equipment Cost Card */}
              <Card className="border-border bg-muted/30 shadow-2xs hover:border-primary/50 transition-colors">
                <CardContent className="p-5 sm:p-6 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                      <Tag className="size-5" />
                    </div>
                    <Badge variant="outline" className="text-xs font-bold border-primary/30 text-primary bg-primary/5">
                      {lang === "vi" ? "Bộ kit học cụ" : "Hardware Kit"}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      {t.equipmentCard.costLabel}
                    </p>
                    <h3 className="text-lg sm:text-xl font-extrabold text-primary mt-1 font-serif">
                      {t.equipmentCard.costValue}
                    </h3>
                  </div>
                  <div className="p-3 rounded-lg bg-primary/10 border border-primary/20 flex items-start gap-2">
                    <Info className="size-4 text-primary shrink-0 mt-0.5" />
                    <p className="text-xs font-medium text-foreground leading-relaxed">
                      {t.equipmentCard.costNote}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Electronic Device Requirements Card */}
              <Card className="border-border bg-muted/30 shadow-2xs hover:border-accent/50 transition-colors">
                <CardContent className="p-5 sm:p-6 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="size-10 rounded-lg bg-accent/20 text-accent flex items-center justify-center">
                      <Tablet className="size-5" />
                    </div>
                    <Badge variant="outline" className="text-xs font-semibold border-border">
                      {lang === "vi" ? "Thiết bị số" : "Devices"}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      {t.equipmentCard.deviceLabel}
                    </p>
                    <h3 className="text-base sm:text-lg font-bold text-foreground mt-1">
                      {lang === "vi" ? "Không phụ thuộc màn hình" : "Low Screen Dependence"}
                    </h3>
                  </div>
                  <div className="space-y-2 pt-1">
                    <div className="flex items-start gap-2 text-xs sm:text-sm text-foreground">
                      <MonitorX className="size-4 text-primary shrink-0 mt-0.5" />
                      <span>{t.equipmentCard.computerNote}</span>
                    </div>
                    <div className="flex items-start gap-2 text-xs sm:text-sm text-muted-foreground">
                      <CheckCircle className="size-4 text-secondary shrink-0 mt-0.5" />
                      <span>{t.equipmentCard.tabletNote}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* New Block 2: Small-Group Learning Model Section */}
        <div className="mb-12 lg:mb-16">
          <div className="border border-border rounded-xl bg-card p-6 sm:p-8 lg:p-10 shadow-xs">
            <div className="max-w-3xl mb-8">
              <Badge variant="outline" className="text-xs font-bold text-primary mb-2">
                {t.smallGroupModel.badge}
              </Badge>
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground font-serif tracking-tight">
                {t.smallGroupModel.heading}
              </h2>
              <p className="text-xs sm:text-sm font-semibold text-primary tracking-wider uppercase mt-1">
                {t.smallGroupModel.subheading}
              </p>
              <p className="text-sm sm:text-base text-muted-foreground mt-3 leading-relaxed">
                {t.smallGroupModel.intro}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {t.smallGroupModel.models.map((model, idx) => <Card key={model.ratio} data-index={idx} className="border-border bg-card shadow-2xs hover:shadow-md transition-shadow relative overflow-hidden">
                  <CardContent className="p-6 sm:p-7 space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/80 pb-4">
                      <div className="flex items-center gap-2.5">
                        <div className="p-2 rounded-lg bg-primary/10 text-primary">
                          <Users className="size-5" />
                        </div>
                        <span className="text-base sm:text-lg font-bold text-foreground font-serif">
                          {model.ratio}
                        </span>
                      </div>
                      <Badge variant={idx === 0 ? "secondary" : "outline"} className="text-xs font-semibold px-2.5 py-1">
                        {model.tag}
                      </Badge>
                    </div>

                    <div>
                      <h3 className="text-lg font-bold text-foreground">
                        {model.title}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                        {model.desc}
                      </p>
                    </div>

                    <div className="pt-3 border-t border-border/60 space-y-2.5">
                      {model.highlights.map((point, pIdx) => <div key={point} data-index={pIdx} className="flex items-start gap-2.5 text-xs sm:text-sm text-foreground">
                          <CheckCircle className="size-4 text-secondary shrink-0 mt-0.5" />
                          <span className="leading-snug">{point}</span>
                        </div>)}
                    </div>
                  </CardContent>
                </Card>)}
            </div>
          </div>
        </div>

        {/* (3) 12-session structure timeline grouped */}
        <div className="mb-12 lg:mb-16">
          <div className="max-w-2xl mb-8">
            <Badge variant="outline" className="text-xs font-bold text-secondary mb-2">
              {lang === "vi" ? "LỘ TRÌNH CHI TIẾT" : "CURRICULUM FLOW"}
            </Badge>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground font-serif">
              {t.timeline.heading}
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground mt-1">
              {t.timeline.subheading}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {t.timeline.stages.map((stage, idx) => <Card key={stage.step} data-index={idx} className="border-border bg-card shadow-2xs relative flex flex-col justify-between">
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-center justify-between gap-2 border-b border-border/80 pb-3">
                    <span className="text-xs font-extrabold text-primary tracking-widest">
                      GIAI ĐOẠN {stage.step}
                    </span>
                    <Badge variant="secondary" className="text-xs font-semibold">
                      {stage.sessions}
                    </Badge>
                  </div>
                  <h3 className="text-base font-bold text-foreground">
                    {stage.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {stage.desc}
                  </p>
                  <div className="pt-2 border-t border-border/60 space-y-1.5">
                    {stage.focus.map((item, fIdx) => <div key={item} data-index={fIdx} className="flex items-start gap-1.5 text-xs font-medium text-foreground">
                        <CheckCircle className="size-3.5 text-secondary shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </div>)}
                  </div>
                </CardContent>
              </Card>)}
          </div>
        </div>

        {/* (4) 'Phương pháp học' / 'Learning Methodology' 4-step flow */}
        <div className="mb-12 lg:mb-16">
          <div className="border border-border rounded-xl bg-card p-6 sm:p-8 lg:p-10 shadow-xs">
            <div className="max-w-2xl mb-8">
              <Badge variant="outline" className="text-xs font-bold text-accent mb-2">
                {lang === "vi" ? "MÔ HÌNH SƯ PHẠM" : "PEDAGOGICAL METHOD"}
              </Badge>
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground font-serif">
                {t.methodology.heading}
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mt-1">
                {t.methodology.subheading}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {t.methodology.steps.map((step, idx) => {
              const icons = [Lightbulb, Wrench, Cpu, Presentation];
              const IconComponent = icons[idx] || Sparkles;
              return <div key={step.number} data-index={idx} className="p-5 rounded-lg bg-muted/40 border border-border/70 flex flex-col justify-between space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-2xl font-black text-primary/30">
                          {step.number}
                        </span>
                        <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                          <IconComponent className="size-5" />
                        </div>
                      </div>
                      <h3 className="text-base sm:text-lg font-bold text-foreground mb-2">
                        {step.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        {step.desc}
                      </p>
                    </div>
                  </div>;
            })}
            </div>
          </div>
        </div>

        {/* Existing: Skills & Mechanics Showcase */}
        <div className="mb-12 lg:mb-16">
          <div className="border border-border rounded-xl bg-card p-6 sm:p-8 lg:p-10 shadow-xs">
            <div className="max-w-2xl mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground font-serif">
                {t.skillsHeading}
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mt-1">
                {lang === "vi" ? "Tích hợp đa năng giữa tư duy cơ khí vật lý và chuỗi giải thuật cho trẻ mầm non." : "Holistic integration of early mechanical physics and foundational computational logic."}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Engineering Column */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-primary font-bold text-base sm:text-lg border-b border-border pb-3">
                  <Wrench className="size-5" />
                  <h3>{t.engineeringSkillsLabel}</h3>
                </div>
                <div className="flex flex-col gap-3">
                  {t.skills.engineering.map((skill, i) => <div key={skill} data-index={i} className="flex items-center gap-3 p-3.5 rounded-lg bg-muted/60 border border-border/60 hover:border-primary/40 transition-colors">
                      <CheckCircle2 className="size-5 text-primary shrink-0" />
                      <span className="text-sm sm:text-base font-semibold text-foreground">
                        {skill}
                      </span>
                    </div>)}
                </div>
              </div>

              {/* Coding Column */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-secondary font-bold text-base sm:text-lg border-b border-border pb-3">
                  <Cpu className="size-5" />
                  <h3>{t.codingSkillsLabel}</h3>
                </div>
                <div className="flex flex-col gap-3">
                  {t.skills.coding.map((skill, i) => <div key={skill} data-index={i} className="flex items-center gap-3 p-3.5 rounded-lg bg-muted/60 border border-border/60 hover:border-secondary/50 transition-colors">
                      <CheckCircle2 className="size-5 text-secondary shrink-0" />
                      <span className="text-sm sm:text-base font-semibold text-foreground">
                        {skill}
                      </span>
                    </div>)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Existing: City Highlights Mini-Grid & Technical Mechanism Illustration */}
        <div className="mb-12 lg:mb-16">
          <h2 className="text-xl sm:text-2xl font-bold text-foreground font-serif mb-6">
            {t.cityHighlightsHeading}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            {t.cityHighlights.map((highlight, idx) => <Card key={highlight.title} data-index={idx} className="border-border bg-card hover:shadow-md transition-shadow">
                <CardContent className="p-5 sm:p-6 space-y-2.5">
                  <span className="text-xs font-bold text-primary tracking-wider uppercase">
                    {lang === "vi" ? `Dự án 0${idx + 1}` : `Project 0${idx + 1}`}
                  </span>
                  <h3 className="text-base sm:text-lg font-bold text-foreground">
                    {highlight.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {highlight.desc}
                  </p>
                </CardContent>
              </Card>)}
          </div>

          {/* Technical Gear-and-Lever Mechanism Illustration Card */}
          <Card className="border-border bg-card overflow-hidden shadow-xs border">
            <CardContent className="p-0">
              <div className="grid grid-cols-1 md:grid-cols-12 items-center">
                <div className="md:col-span-5 p-3 sm:p-4">
                  <div className="overflow-hidden rounded-lg border border-border bg-muted">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/duplo_d2_gear_lever_lifting_mechanism.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/duplo_d2_gear_lever_lifting_mechanism.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/duplo_d2_gear_lever_lifting_mechanism.png-wvc-sizes" alt="Illustration of a Duplo gear and lever mechanism lifting a city bridge structure" loading="lazy" decoding="async" className="w-full h-48 sm:h-56 object-cover" />
                  </div>
                </div>
                <div className="md:col-span-7 p-5 sm:p-6 space-y-2.5">
                  <Badge variant="outline" className="text-xs font-bold text-primary border-primary/20 bg-primary/10">
                    {lang === "vi" ? "MÔ PHỎNG CƠ CẤU TRỌNG TÂM D2" : "D2 KEY MECHANISM ILLUSTRATION"}
                  </Badge>
                  <h3 className="text-lg sm:text-xl font-bold text-foreground font-serif">
                    {lang === "vi" ? "Cơ cấu bánh răng & đòn bẩy nâng hạ cầu đường" : "Duplo Gear & Lever Lifting Mechanism"}
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {lang === "vi" ? "Mô phỏng chi tiết nguyên lý truyền động cơ học kết hợp giữa bộ bánh răng ăn khớp và thanh đòn bẩy trợ lực, giúp học sinh D2 dễ dàng làm chủ cơ chế điều khiển rào chắn, cầu xoay và ròng rọc trong các dự án đô thị." : "Demonstrating gear mesh physics and mechanical lever leverage in detail, allowing D2 students to master swing bridges, railroad gates, and construction hoists across city projects."}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Final Project Card & Platform Spec Card */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12 lg:mb-16">
          {/* Final Project Card: 'Thành phố tí hon' / 'Tiny City' */}
          <div className="lg:col-span-7">
            <Card className="border-border bg-card shadow-xs h-full flex flex-col justify-between">
              <CardContent className="p-6 sm:p-8 space-y-5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Badge variant="outline" className="text-xs font-bold border-primary/30 text-primary bg-primary/5">
                    {t.finalProject.badge}
                  </Badge>
                  <span className="text-xs text-muted-foreground font-semibold">
                    12 SESSIONS CAPSTONE
                  </span>
                </div>

                <div>
                  <h3 className="text-2xl sm:text-3xl font-extrabold text-foreground font-serif">
                    {t.finalProject.projectName}
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground mt-2 leading-relaxed">
                    {t.finalProject.desc}
                  </p>
                </div>

                <div className="space-y-2.5 pt-2 border-t border-border">
                  <p className="text-xs font-bold uppercase tracking-wider text-primary">
                    {lang === "vi" ? "Đặc điểm cơ cấu tích hợp:" : "Integrated Mechanism Highlights:"}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {t.finalProject.highlights.map((point, idx) => <div key={point} data-index={idx} className="flex items-start gap-2 p-2.5 rounded-lg bg-muted/40 border border-border/50 text-xs sm:text-sm font-medium text-foreground">
                        <CheckCircle className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{point}</span>
                      </div>)}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* 'Nền tảng / thiết bị' Spec Card: Platform = Duplo */}
          <div className="lg:col-span-5">
            <Card className="border-border bg-card shadow-xs h-full flex flex-col justify-between">
              <CardContent className="p-6 sm:p-8 space-y-5">
                <div className="flex items-center justify-between gap-2">
                  <Badge variant="secondary" className="text-xs font-semibold">
                    {t.platformSpec.badge}
                  </Badge>
                  <Boxes className="size-5 text-secondary" />
                </div>

                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-foreground font-serif">
                    {t.platformSpec.heading}
                  </h3>
                  <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-secondary/15 text-secondary font-bold text-base">
                    <ShieldCheck className="size-4" />
                    <span>{t.platformSpec.platformName}</span>
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground mt-3 leading-relaxed">
                    {t.platformSpec.desc}
                  </p>
                </div>

                <div className="space-y-2 pt-2 border-t border-border">
                  {t.platformSpec.specs.map((item, idx) => <div key={item.label} data-index={idx} className="flex items-center justify-between text-xs sm:text-sm py-1.5 border-b border-border/40 last:border-b-0">
                      <span className="text-muted-foreground font-medium">
                        {item.label}
                      </span>
                      <span className="font-semibold text-foreground text-right">
                        {item.value}
                      </span>
                    </div>)}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* 'Vị trí trong lộ trình' / 'Position in Pathway' mini roadmap */}
        <div className="mb-12 lg:mb-16">
          <div className="border border-border rounded-xl bg-card p-6 sm:p-8 lg:p-10 shadow-xs">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
              <div>
                <Badge variant="outline" className="text-xs font-bold text-primary mb-2">
                  {lang === "vi" ? "BẢN ĐỒ TIẾN TRÌNH" : "MINI ROADMAP"}
                </Badge>
                <h2 className="text-2xl sm:text-3xl font-bold text-foreground font-serif">
                  {t.pathwayMap.heading}
                </h2>
                <p className="text-sm sm:text-base text-muted-foreground mt-1">
                  {t.pathwayMap.subheading}
                </p>
              </div>
              <Badge variant="secondary" className="self-start sm:self-auto text-xs font-bold px-3 py-1">
                {t.pathwayMap.currentLabel}
              </Badge>
            </div>

            {/* 10-Node Pathway Track with Thumbnails */}
            <div className="grid grid-cols-2 sm:grid-cols-5 lg:grid-cols-10 gap-2.5">
              {t.pathwayMap.nodes.map((node, idx) => {
              const thumb = COURSE_THUMBNAILS[node.code];
              if (node.isCurrent) {
                return <div key={node.code} data-index={idx} className="p-2.5 sm:p-3 rounded-lg border-2 border-primary bg-primary/10 shadow-xs flex flex-col items-center justify-center text-center relative">
                      {thumb && <img src={thumb.src} data-wvc-srcset={thumb.srcset} data-wvc-sizes={thumb.sizes} alt={`${node.code} robotics course thumbnail`} loading="lazy" decoding="async" className="size-9 sm:size-10 object-cover rounded-md mb-1.5 border border-primary/30 shrink-0" />}
                      <span className="text-xs font-black text-primary tracking-wider">
                        {node.code}
                      </span>
                      <span className="text-xs font-bold text-foreground mt-0.5 line-clamp-2">
                        {node.name}
                      </span>
                      <span className="mt-1 text-[10px] font-semibold text-primary uppercase bg-primary/20 px-1.5 py-0.5 rounded-md">
                        {lang === "vi" ? "Hiện tại" : "Current"}
                      </span>
                    </div>;
              }
              return <Link key={node.code} to={node.url || "/RoboticsLearningPathwayPage"} data-index={idx} className="p-2.5 sm:p-3 rounded-lg border border-border bg-muted/40 hover:bg-card hover:border-primary/40 hover:shadow-xs transition-all flex flex-col items-center justify-center text-center group">
                    {thumb && <img src={thumb.src} data-wvc-srcset={thumb.srcset} data-wvc-sizes={thumb.sizes} alt={`${node.code} robotics course thumbnail`} loading="lazy" decoding="async" className="size-9 sm:size-10 object-cover rounded-md mb-1.5 border border-border/60 group-hover:border-primary/40 shrink-0 transition-colors" />}
                    <span className="text-xs font-bold text-muted-foreground group-hover:text-primary transition-colors">
                      {node.code}
                    </span>
                    <span className="text-xs font-medium text-foreground mt-0.5 line-clamp-2 group-hover:text-primary transition-colors">
                      {node.name}
                    </span>
                  </Link>;
            })}
            </div>
          </div>
        </div>

        {/* 'Bước tiếp theo của con là gì?' card */}
        <div className="mb-12">
          <Card className="border-border bg-gradient-to-r from-card via-card to-primary/5 shadow-xs">
            <CardContent className="p-6 sm:p-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="space-y-2 max-w-2xl">
                <Badge variant="outline" className="text-xs font-bold text-primary border-primary/20 bg-primary/10">
                  {t.whatsNext.courseCode}
                </Badge>
                <h3 className="text-xl sm:text-2xl font-bold text-foreground font-serif">
                  {t.whatsNext.heading}
                </h3>
                <p className="text-base font-semibold text-primary">
                  {t.whatsNext.courseTitle}
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {t.whatsNext.desc}
                </p>
              </div>
              <Button asChild size="lg" className="rounded-md shrink-0 shadow-sm">
                <Link to="/RoboticsCourseD3Page">
                  <span>{t.whatsNext.cta}</span>
                  <ArrowRight className="size-4 ml-2" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>

        <Separator className="my-8" />

        {/* Pathway Sequential Course Navigation */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4">
          <Button asChild variant="outline" size="lg" className="w-full sm:w-auto rounded-md border-border justify-start font-semibold text-foreground hover:bg-muted">
            <Link to="/RoboticsCourseD1Page">
              <span className="truncate">{t.prevCourse}</span>
            </Link>
          </Button>

          <Button asChild size="lg" className="w-full sm:w-auto rounded-md bg-primary text-primary-foreground font-semibold justify-end shadow-xs hover:bg-primary/90">
            <Link to="/RoboticsCourseD3Page">
              <span className="truncate">{t.nextCourse}</span>
              <ArrowRight className="size-4 ml-2" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}